import {type SystemNotification} from '@qep/common';
import {getQepSystem, loadAlgos} from '@qep/shared/src';
import {
  createAsyncThunk, createAction, createSlice, type Slice,
} from '@reduxjs/toolkit';
import {type IJsonModel} from 'flexlayout-react';

const getAppWorker = () =>
  getQepSystem().container?.applicationClient;

export type RootState = {
  application: SliceState;
};

type SliceState = {
  loading: boolean;
  activeWorkspace: Partial<IJsonModel>;
  systemNotifications: Array<SystemNotification & {acknowledged: boolean}>;
};

const initialState: SliceState = {
  loading: true,
  activeWorkspace: {},
  systemNotifications: [],
};

export const loadAlgoDefinitions = createAsyncThunk(
  'application/loadAlgoDefinitions',
  async (_noop: string, {getState: _getState, dispatch: _dispatch}) => {
    await loadAlgos();
  },
);

export const loadInitialState = createAsyncThunk(
  'application/loadInitialState',
  async (_noop: string, {getState: _getState, dispatch: _dispatch}) => {
    const workspace = await getAppWorker().getActiveWorkspace();
    const workspaceMessage = workspace.message as IJsonModel & {error?: unknown; status?: string};
    const hasStorageError = workspaceMessage?.error !== undefined;
    const isUnsuccessful = workspaceMessage?.status === 'unsuccessful';
    if (hasStorageError || isUnsuccessful) {
      throw new Error('Workspace storage request failed');
    }

    return {workspace: workspaceMessage as IJsonModel};
  },
);
const randomString = Math.random().toString(36).slice(7);
export const saveWorkspaceState = createAsyncThunk(
  'application/updateworkspacestate',
  async (workspace: IJsonModel, {getState: _getState, dispatch: _dispatch}) => {
    (workspace as any).windowID = randomString;
    await getAppWorker().saveworkspace(workspace);
    return {workspace};
  },
);

export const activateSystemNotification = createAction<SystemNotification>('activate-system-notification');
export const deactivateSystemNotification = createAction<string>('deactivate-system-notification');
export const setSystemNotifications = createAction<SystemNotification[]>('set-system-notifications');

// eslint-disable-next-line unicorn/prevent-abbreviations
export const applicationReducer: Slice = createSlice({
  name: 'application',
  initialState,
  extraReducers(builder) {
    builder.addCase(loadInitialState.fulfilled, (state, {payload}) => {
      state.activeWorkspace = payload.workspace;
    });
    builder.addCase(activateSystemNotification, (state, {payload}) => {
      state.systemNotifications = state.systemNotifications.filter(n => n.id !== payload.id);

      const notification = {...payload, acknowledged: false};
      if (payload.level === 'critical') {
        state.systemNotifications.unshift(notification);
      } else {
        state.systemNotifications.push(notification);
      }
    });
    builder.addCase(deactivateSystemNotification, (state, {payload}) => {
      state.systemNotifications = state.systemNotifications.filter(n => n.id !== payload);
    });
    builder.addCase(setSystemNotifications, (state, {payload}) => {
      state.systemNotifications = payload.map(n => ({...n, acknowledged: false}));
    });
  },
  reducers: {
    updateWorkspaceState(state, action: {payload: IJsonModel}) {
      state.activeWorkspace = action.payload;
    },
    acknowledgeSystemNotification(state: SliceState, action: {payload: string}) {
      const notification = state.systemNotifications.find(n => n.id === action.payload);
      if (notification) {
        notification.acknowledged = true;
      }
    },
  },
});

export default applicationReducer.reducer;
export const selectWorkspaceChange = (state: RootState) => state.application.activeWorkspace;

export const selectSystemNotifications = (state: RootState) => state.application.systemNotifications;
